import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import * as ElementModels from '../../../models/report-view.models';
import { Chart } from 'angular-highcharts';
import { SeriesOptionsType } from 'highcharts';
import { ChartService } from '../../chart/chart.service';
import { map, takeWhile } from 'rxjs/operators';
import { getChartColors } from '../panel-utils';
import { stackedColumnChartPlaceholder } from '../../../constants/highcharts-placeholders';
import { ElementBaseComponent } from '../ElementBaseComponent';
import { HelpTextService } from '../../../services/services-index';

@Component({
  selector: 'stacked-column-chart',
  templateUrl: './stacked-column-chart.component.html',
  styleUrls: ['../panel-elements.scss'],
  standalone: false
})

export class StackedColumnChartComponent extends ElementBaseComponent implements ElementModels.ElementComponent, OnInit, OnDestroy {
  @Input() set dataSets(value: ElementModels.DataSet[]) {
    if (!!value && value.length > 0) {
      this.processData(value[0]);
      this.isDataSetLoaded$.next(true);
    } else {
      this.isDataSetLoaded$.next(false);
    }
  }

  @Input() selectedMetric: string;

  componentActive: boolean = true;
  currentChart: any;
  primaryMetricName: string;
  primaryMetricDisplayValue: string;

  placeholderChart: Chart;
  constructor(
    private chartService: ChartService,
    helpTextService: HelpTextService
  ) {
    super(helpTextService);
  }

  ngOnInit() {
    this.initializePlaceholder();

    this.chartService.reflowChart$.pipe(
      takeWhile(() => this.componentActive),
      map(() => {
        setTimeout(() => {
          if (this.currentChart && this.currentChart.ref) {
            this.currentChart.ref.reflow();
          }
        }, 200);
      })
    ).subscribe()
  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  private processData(dataSet: ElementModels.DataSet): void {
    const dimensionNames = this.getDimensionNames();

    if (!dimensionNames.length) {
      throw new Error('Dimension is required.');
    }

    const dateIndex = dataSet.columns.findIndex(col => col.name === 'Date');
    const dimIndex = dataSet.columns.findIndex(col => col.name === dimensionNames[0]);
    const labelDimIndex = dimensionNames.length > 1
      ? dataSet.columns.findIndex(col => col.name === dimensionNames[1])
      : -1;
    const metricIndex = dataSet.columns.findIndex(col => col.name === this.selectedMetric);

    if (dateIndex === -1 || dimIndex === -1 || metricIndex === -1) {
      throw new Error('Invalid dimension or metric selection.');
    }

    const categories = [];
    const seriesNames = [];

    dataSet.rows.forEach(row => {
      const dimCell = row[dimIndex];

      if (!dimCell?.value || dimCell.label === ''
        || this.settings.dimensionRowFilters?.some(f => f === dimCell.value || f === dimCell.label))
        return;

      if (!row[metricIndex].value)
        return;

      const date = this.getCellDisplayValue(row[dateIndex]);
      const dim = this.getCellDisplayValue(dimCell);

      if (!categories.includes(date)) {
        categories.push(date);
      }

      // sort categories by date
      categories.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      if (!seriesNames.includes(dim)) {
        seriesNames.push(dim);
      }
    });

    const seriesData = seriesNames.map(seriesName => {
      const data = categories.map(category => {
        const row = dataSet.rows.find(row => {
          const date = this.getCellDisplayValue(row[dateIndex]);
          const dim = this.getCellDisplayValue(row[dimIndex]);
          return date === category && dim === seriesName
        });
        if (!row) {
          return {
            name: seriesName,
            y: 0,
            label: 0,
            pointDisplay: this.buildPointTooltipLabel(seriesName, seriesName)
          };
        }

        const pointLabel = labelDimIndex >= 0 ? this.getCellDisplayValue(row[labelDimIndex]) : seriesName;
        return {
          name: seriesName,
          y: (row[metricIndex].value as number) || 0,
          label: row[metricIndex].label || row[metricIndex].value || 0,
          pointDisplay: this.buildPointTooltipLabel(pointLabel, seriesName)
        };
      });
      return { name: seriesName, data };
    });

    // convert categories to date objects
    const dateCategories = categories.map(category => new Date(category));

    this.currentChart = this.generateChart(seriesData, dateCategories);
  }

  private generateChart(seriesData: any[], categories: any[]) {
    const colors = getChartColors(this.settings);

    const chartSeries: SeriesOptionsType[] = [{
      type: 'column',
      data: seriesData
    }];

    var chart = new Chart({
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      colors: colors,
      chart: {
        type: 'column',
        //showAxes: false,
        height: 400
      },
      // AXES.....
      xAxis: {
        categories: categories,
        type: 'datetime',
        dateTimeLabelFormats: {
          day: '%m/%d/%Y'
        },
        labels: {
          formatter: function () {
            const date = new Date(this.value);
            return date.toLocaleDateString();
          }
        }
        // title: { text: null },
        // lineWidth: 0,
        // minorGridLineWidth: 0,
        // lineColor: 'transparent',
        // minorTickLength: 0,
        // tickLength: 0,
      },
      yAxis: {
        //tickInterval: 20,
        // labels: {
        //   format: '{value}%'
        // },
        title: {
          text: ''
        },
        stackLabels: {
          enabled: false
        }
      },
      tooltip: {
        formatter: function () {
          const point = <any>this.point;
          const pointDisplay = point.pointDisplay || this.series.name;
          return `<b>${new Date(this.x).toLocaleDateString()}</b><br/>${pointDisplay}: ${point.label}<br> % of Total: <b>${(this.point.percentage).toFixed(2)}%</b>`;
        },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          dataLabels: {
            enabled: false
          }
        }
      },
      legend: {
        enabled: true,
        verticalAlign: 'bottom'
      },
      series: seriesData
    });

    return chart;
  }

  // generateChartColors(darkColor: string, lightColor: string, length: number): string[] {
  //   const colors = chroma.scale([darkColor, lightColor])
  //     .mode('lch')
  //     .colors(length);

  //   return colors;
  // }

  private initializePlaceholder(): void {
    if (this.placeholderChart) {
      // exit if chart is already initialized
      return;
    }

    // without setTimeout the placeholder chart
    // won't respect the size of the parent container
    setTimeout(() => {
      this.placeholderChart = new Chart(stackedColumnChartPlaceholder);
    }, 0);
  }

  private getCellDisplayValue(cell: ElementModels.RowValue | undefined): string {
    if (!cell) {
      return '';
    }

    if (cell.label !== undefined && cell.label !== null && cell.label !== '') {
      return String(cell.label);
    }

    if (cell.value !== undefined && cell.value !== null) {
      return String(cell.value);
    }

    return '';
  }

  private buildPointTooltipLabel(name: string, code: string): string {
    if (name && code && name !== code) {
      return `${name} (${code})`;
    }

    return name || code || '';
  }
}
